import { ChartData, ChartOptions } from 'chart.js'
import React from 'react'
import { Chart } from 'react-chartjs-2'

import { GranularityEnum, IGenericBucket } from '../types'
import { formatNumber, IBaseBuildOptions } from '../utils'

import {
  getAdditionalLegend,
  getChartPlugins,
  getDefaultOptions,
  getLabelsWithGranularity,
  getXTicks,
  getYTicks,
  IGraphProps,
  STYLE_CHART_CONTAINER,
} from './graphCommons'
import { useGraphContext } from './GraphWrapper/GraphWrappers'
import { DEFAULT_DATASET_STYLES } from './themes'

interface IBuildOptions extends IBaseBuildOptions {
  enableAnimation: boolean
  maxValue?: number
}

export const buildOptions = ({
  title,
  showTitle,
  formatWithUnit = (x) => x,
  displayLegend = true,
  legendPosition = 'bottom',
  yTicksFormatter,
  enableAnimation,
  maxValue = 0,
}: IBuildOptions): ChartOptions => {
  const options: ChartOptions = {
    ...getDefaultOptions({
      title,
      showTitle,
      displayLegend,
      legendPosition,
      enableAnimation,
    }),
    hover: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      y: {
        max: maxValue > 0 ? maxValue : undefined,
        ticks: getYTicks(formatWithUnit, yTicksFormatter),
      },
      x: {
        max: undefined,
        ticks: getXTicks(),
      },
    },
  }
  options.plugins!.tooltip = {
    mode: 'index',
    intersect: false,
    callbacks: {
      label: (tooltipItem: any) => {
        const dataset = tooltipItem.dataset
        // Additional legend to display in tooltip, after the value
        const additionalLegend = getAdditionalLegend(dataset, tooltipItem.dataIndex, '')
        const label = yTicksFormatter ? yTicksFormatter(tooltipItem.raw) : formatNumber(tooltipItem.raw)

        return `${dataset.label}: ${formatWithUnit(label)}${additionalLegend}`
      },
    },
  }
  return options
}

function getData(buckets: IGenericBucket[], granularity: GranularityEnum, label: string) {
  return {
    labels: getLabelsWithGranularity(buckets, granularity),
    datasets: [
      {
        ...DEFAULT_DATASET_STYLES[0],
        label,
        data: buckets.map((b: any): number => b.doc_count || 0),
        tension: 0.4,
      },
    ],
  } as ChartData<'line'>
}

interface IProps extends IGraphProps {
  enableAnimation?: boolean
  granularity: GranularityEnum
  label: string
}

function LinePlot({
  title = '',
  showTitle,
  label,
  w,
  h,
  maxValue,
  rawData,
  formatWithUnit,
  granularity,
  enableAnimation = true,
  displayLegend = true,
  xTicksFormatter,
}: IProps) {
  const { reactChart } = useGraphContext()

  const plugins = getChartPlugins()

  return (
    <div style={STYLE_CHART_CONTAINER}>
      <div>
        <Chart
          title={title}
          ref={reactChart.chartRef}
          type="line"
          data={getData(rawData, granularity, label)}
          width={w}
          height={h}
          options={
            buildOptions({
              title,
              showTitle,
              formatWithUnit,
              displayLegend,
              maxValue,
              xTicksFormatter,
              enableAnimation,
            }) as ChartOptions<'line'>
          }
          plugins={plugins}
        />
      </div>
    </div>
  )
}

export default LinePlot
